import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Button from "../../../../components/ui/Button";
import Text from "../../../../components/ui/Text";

export const FeatureSection = ({
  id,
  title,
  subtitle,
  description,
  buttonText,
  imgSrc,
  toast,
  reverse = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.4,
        rootMargin: "0px 0px -50px 0px",
      },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <Box
      id={id}
      ref={sectionRef}
      sx={{
        display: "flex",
        flexDirection: {
          xs: "column-reverse",
          md: reverse ? "row-reverse" : "row",
        },
        justifyContent: "space-between",
        height: { md: "calc(100dvh - 80px)" },
      }}
    >
      <Box
        sx={{
          width: { md: "50%" },
          backgroundColor: "#ffffff",
          display: "flex",
          justifyContent: reverse ? "flex-start" : "flex-end",
          alignItems: "center",
          paddingTop: { xs: 6, md: 0 },
          paddingBottom: { xs: 6, md: 0 },
        }}
      >
        <Box
          sx={{
            maxWidth: 600,
            px: { xs: 2, md: 4 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Text
            variant="subtitle3"
            component="h2"
            sx={{
              textTransform: "uppercase",
              fontSize: { xs: "16px", md: "20px" },
              color: "#203FA9",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.8s ease-out 0.2s",
            }}
          >
            {title}
          </Text>
          <Text
            component="h3"
            sx={{
              mt: { xs: 1, md: 2 },
              fontSize: { xs: "28px", sm: "5.5vw", md: "42px" },
              fontWeight: 900,
              color: "#171717",
              lineHeight: "100%",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.8s ease-out 0.4s",
            }}
          >
            {subtitle}
          </Text>
          <Text
            sx={{
              mt: { xs: 4, md: 6 },
              fontSize: "18px",
              color: "#171717",
              lineHeight: "125%",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.8s ease-out 0.6s",
            }}
          >
            {description}
          </Text>

          <Button
            component={Link}
            to="/create-game"
            sx={{
              fontWeight: 500,
              mt: 8,
              fontSize: { xs: 18, md: 20 },
              height: { xs: 56, md: 48, lg: 56 },
              width: { xs: "100%", sm: "fit-content" },
              padding: { xs: "0 24px", md: "0 32px" },
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.8s ease-out 0.8s",
            }}
          >
            {buttonText}
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          width: { md: "50%" },
          aspectRatio: { xs: 1.15, md: "auto" },
          backgroundColor: "#F2F4F8",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          overflow: "hidden",
          position: "relative",
          paddingTop: { xs: 4, md: 0 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: { md: "80%" },
            width: { xs: "55%", md: "100%" },
            aspectRatio: { xs: 0.62, md: "auto" },
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(300px)",
            transition: "all 1s ease-out 0.4s",
          }}
        >
          <img
            src={imgSrc}
            alt="Feature Section"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: toast.position,
            right: toast.right || "5%",
            width: toast?.width || 120,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateX(0)" : "translateX(100px)",
            transition: "all 0.3s ease-out 2s",
          }}
        >
          <img
            src={toast?.imgSrc}
            alt="Toast"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </Box>
      </Box>
    </Box>
  );
};
